import { useState } from "react";
import { ScanLine } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { METRIC_LABELS } from "@/lib/format";
import { Button } from "@/components/portal/Ui";

/** Reads the numbers printed on uploaded insight screenshots and hands them back for pre-fill. */
export function ScreenshotReader({ fileIds, platform, onRead, testId = "screenshot-reader" }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/insights/read-screenshots", {
        file_ids: fileIds,
        platform,
      });
      setResult(data);
      onRead(data);
      const found = Object.keys(data.values).length;
      if (found === 0) toast.error("Nothing readable in that screenshot — enter the numbers below.");
      else toast.success(`Read ${found} metric${found === 1 ? "" : "s"} from the screenshot`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const ready = fileIds.length > 0 && Boolean(platform);
  return (
    <div className="rounded-xl border border-[#EFEAE1] bg-[#FBFAF7] p-3.5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div>
          <p className="text-sm font-semibold">Read the numbers from the screenshot</p>
          <p className="mt-0.5 text-xs body-text">
            {ready
              ? "Fills the fields below from what the screenshot shows. Check every figure before you save."
              : "Attach a screenshot first, then read the numbers straight off it."}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={run}
          disabled={!ready}
          busy={busy}
          testId={`${testId}-run`}
        >
          <ScanLine className="h-4 w-4" />
          {busy ? "Reading…" : "Read screenshot"}
        </Button>
      </div>
      {result && (
        <div className="mt-2.5 text-xs" data-testid={`${testId}-result`}>
          {result.unreadable.length > 0 ? (
            <p className="font-semibold text-[#A85A05]">
              Could not read {result.unreadable.map((k) => METRIC_LABELS[k] || k).join(", ")} —
              enter {result.unreadable.length === 1 ? "it" : "those"} yourself, or tick
              &quot;Not available&quot; if the platform does not show{" "}
              {result.unreadable.length === 1 ? "it" : "them"}.
            </p>
          ) : (
            <p className="font-semibold text-[#1B6B3A]">
              Every metric was read from the screenshot. Check them before you save.
            </p>
          )}
          {result.confidence === "low" && Object.keys(result.values).length > 0 && (
            <p className="mt-1 font-semibold text-[#A85A05]">
              The screenshot was hard to read — check every figure against the platform before
              saving.
            </p>
          )}
          {result.note && <p className="mt-1 body-text">{result.note}</p>}
        </div>
      )}
    </div>
  );
}
